import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useWatchdogHealth } from '@/hooks/useWatchdogHealth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, CreditCard, Users, Store, Rss, Zap, Settings as SettingsIcon } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import GeneralTab from '@/components/settings/GeneralTab';
import SubscriptionTab from '@/components/settings/SubscriptionTab';
import GoogleAccounts from './GoogleAccounts';
import MerchantCenters from './MerchantCenters';
import Feeds from './Feeds';
import Schedules from './Schedules';

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  return `${Math.floor(seconds / 3600)} h`;
}

const VALID_TABS = ['general', 'subscription', 'accounts', 'merchants', 'feeds', 'schedules'];

const Settings: React.FC = () => {
  const { t } = useTranslation('settings');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'general';
  const activeTab = VALID_TABS.includes(tabParam) ? tabParam : 'general';

  const { deadWatchdogs, hasDeadWatchdog } = useWatchdogHealth();

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      {hasDeadWatchdog && (
        <div className="flex gap-3 items-start rounded-md border-l-4 border-warning bg-warning-light p-4">
          <AlertTriangle className="h-5 w-5 text-warning-ink mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-warning-ink">
              {t('watchdog.title')}
            </div>
            <div className="text-warning-ink mt-1">
              {t('watchdog.description')}
            </div>
            <ul className="text-warning-ink mt-2 list-disc list-inside text-xs">
              {deadWatchdogs.map((w) => (
                <li key={w.name}>
                  <code className="font-mono">{w.name}</code>:{' '}
                  {t('watchdog.lastActive', {
                    age: formatAge(w.ageSeconds),
                    interval: w.expectedIntervalSeconds,
                  })}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="w-full flex overflow-x-auto h-auto flex-wrap gap-1 bg-muted/50 p-1">
          <TabsTrigger value="general" className="gap-1.5 text-xs sm:text-sm">
            <SettingsIcon className="h-3.5 w-3.5 hidden sm:block" />
            {t('tabs.general')}
          </TabsTrigger>
          <TabsTrigger value="subscription" className="gap-1.5 text-xs sm:text-sm">
            <CreditCard className="h-3.5 w-3.5 hidden sm:block" />
            {t('tabs.subscription')}
          </TabsTrigger>
          <TabsTrigger value="accounts" className="gap-1.5 text-xs sm:text-sm">
            <Users className="h-3.5 w-3.5 hidden sm:block" />
            {t('tabs.accounts')}
          </TabsTrigger>
          <TabsTrigger value="merchants" className="gap-1.5 text-xs sm:text-sm">
            <Store className="h-3.5 w-3.5 hidden sm:block" />
            {t('tabs.merchants')}
          </TabsTrigger>
          <TabsTrigger value="feeds" className="gap-1.5 text-xs sm:text-sm">
            <Rss className="h-3.5 w-3.5 hidden sm:block" />
            {t('tabs.feeds')}
          </TabsTrigger>
          <TabsTrigger value="schedules" className="gap-1.5 text-xs sm:text-sm">
            <Zap className="h-3.5 w-3.5 hidden sm:block" />
            {t('tabs.schedules')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <GeneralTab />
        </TabsContent>

        <TabsContent value="subscription">
          <SubscriptionTab />
        </TabsContent>

        <TabsContent value="accounts">
          <GoogleAccounts />
        </TabsContent>

        <TabsContent value="merchants">
          <MerchantCenters />
        </TabsContent>

        <TabsContent value="feeds">
          <Feeds />
        </TabsContent>

        <TabsContent value="schedules">
          <Schedules embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
